import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, formatDate, todayISO } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FYPicker, { currentFYStart } from "@/components/FYPicker";
import { Percent } from "lucide-react";

const STATUS = {
  paid: "bg-green-50 text-green-800 border-green-200",
  pending: "bg-yellow-50 text-yellow-800 border-yellow-200",
  late: "bg-red-50 text-red-700 border-red-200",
};

export default function LoanTracker() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const [loans, setLoans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [interestReport, setInterestReport] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ month_key: "", amount: "", date: todayISO() });
  const [tab, setTab] = useState("given");  // "given" | "taken"
  const [view, setView] = useState("schedule"); // "schedule" | "interest"
  const [fy, setFy] = useState(currentFYStart());

  useEffect(() => {
    api.get("/investments").then((r) => {
      const l = r.data.filter((i) => i.type === "loan" || i.type === "loan_taken");
      setLoans(l);
    });
  }, []);

  // Auto-pick first loan matching the active tab
  useEffect(() => {
    const wanted = tab === "given" ? "loan" : "loan_taken";
    const match = loans.filter((l) => l.type === wanted);
    if (match.length && !match.find((l) => l.id === selected)) {
      setSelected(match[0].id);
    } else if (match.length === 0) {
      setSelected(null);
      setData(null);
      setInterestReport(null);
    }
  }, [tab, loans]);   // eslint-disable-line

  const filteredLoans = loans.filter((l) => l.type === (tab === "given" ? "loan" : "loan_taken"));

  useEffect(() => {
    if (!selected) return;
    setData(null); setInterestReport(null);
    const q = fy === "all" ? "" : `?fy=${fy}`;
    api.get(`/loans/${selected}/payments${q}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load loan schedule");
        setData(null);
      });
    if (view === "interest") {
      api.get(`/loans/${selected}/interest-report${q}`)
        .then((r) => setInterestReport(r.data))
        .catch(() => setInterestReport(null));
    }
  }, [selected, fy, view]);

  const openDlg = (row) => {
    setForm({ month_key: row.month_key, amount: row.interest_due, date: todayISO() });
    setDlg(true);
  };

  const save = async () => {
    try {
      await api.post(`/loans/${selected}/payments`, { ...form, amount: Number(form.amount) });
      toast.success("Payment recorded");
      setDlg(false);
      const q = fy === "all" ? "" : `?fy=${fy}`;
      const r = await api.get(`/loans/${selected}/payments${q}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!loans.length) {
    return <div className="text-slate-500 text-sm">No loans on record. Add one from Investments → New (choose “Loan Given” or “Loan Taken”).</div>;
  }
  if (!selected || !data) {
    return (
      <div className="space-y-6">
        <div>
          <div className="eyebrow">Loan Book</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="loan-title">
            Loan <em className="italic font-light">tracker.</em>
          </h1>
        </div>
        <TabBar tab={tab} setTab={setTab} givenCount={loans.filter((l) => l.type === "loan").length} takenCount={loans.filter((l) => l.type === "loan_taken").length} />
        <div className="text-slate-500 text-sm">
          No {tab === "given" ? "loans given" : "loans taken"} yet — add one from Investments → New.
        </div>
      </div>
    );
  }

  const s = data.summary;
  const isTaken = tab === "taken";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Loan Book</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="loan-title">
            Loan <em className="italic font-light">tracker.</em>
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FYPicker value={fy} onChange={setFy} size="sm" />
          <Select value={selected || ""} onValueChange={setSelected}>
            <SelectTrigger className="w-72" data-testid="loan-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              {filteredLoans.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <TabBar tab={tab} setTab={setTab}
          givenCount={loans.filter((l) => l.type === "loan").length}
          takenCount={loans.filter((l) => l.type === "loan_taken").length} />
        <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md p-1">
          <button onClick={() => setView("schedule")} data-testid="view-schedule"
            className={`px-3 py-1.5 rounded text-[12px] font-medium transition-colors ${view === "schedule" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            Schedule
          </button>
          <button onClick={() => setView("interest")} data-testid="view-interest"
            className={`px-3 py-1.5 rounded text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${view === "interest" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            <Percent className="h-3 w-3" /> Interest report
          </button>
        </div>
      </div>

      {view === "schedule" && (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-5 border border-slate-200 bg-white">
          <div className="text-[10px] tracking-wider uppercase text-slate-500">{isTaken ? "Loan Received" : "Loan Given"}</div>
          <div className="mt-2 font-mono-fin text-2xl font-semibold text-slate-900" data-testid="loan-given">{formatRupeesCompact(s.loan_given)}</div>
        </div>
        <div className="p-5 border border-slate-200 bg-white">
          <div className="text-[10px] tracking-wider uppercase text-slate-500">{isTaken ? "Interest Paid" : "Interest Received"}</div>
          <div className={`mt-2 font-mono-fin text-2xl font-semibold ${isTaken ? "text-red-600" : "text-green-700"}`} data-testid="loan-received">{formatRupeesCompact(s.interest_received_total)}</div>
        </div>
        <div className="p-5 border border-slate-200 bg-white">
          <div className="text-[10px] tracking-wider uppercase text-slate-500">Interest Pending</div>
          <div className="mt-2 font-mono-fin text-2xl font-semibold text-yellow-700" data-testid="loan-pending">{formatRupeesCompact(s.interest_pending)}</div>
        </div>
        <div className="p-5 border border-slate-200 bg-white">
          <div className="text-[10px] tracking-wider uppercase text-slate-500">Days Overdue</div>
          <div className={`mt-2 font-mono-fin text-2xl font-semibold ${s.days_overdue > 0 ? "text-red-600" : "text-slate-900"}`} data-testid="loan-overdue">{s.days_overdue}</div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Month</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Interest Due</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Amount Paid</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Date Paid</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Status</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.month_key} className="border-b border-slate-100">
                  <td className="p-3 font-medium">{r.label} {r.year}</td>
                  <td className="p-3 text-right font-mono-fin">{formatRupees(r.interest_due)}</td>
                  <td className={`p-3 text-right font-mono-fin ${r.amount_paid > 0 ? "text-green-700" : "text-slate-400"}`}>
                    {r.amount_paid > 0 ? formatRupees(r.amount_paid) : "-"}
                  </td>
                  <td className="p-3 text-slate-600">{r.date_paid ? formatDate(r.date_paid) : "-"}</td>
                  <td className="p-3">
                    <span className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 border ${STATUS[r.status] || ""}`} data-testid={`loan-status-${r.month_key}`}>
                      {r.status === "late" ? `LATE ${r.days_late}d` : r.status.toUpperCase()}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="p-3 text-right">
                      {r.status !== "paid" && (
                        <button onClick={() => openDlg(r)} className="text-sm text-red-600 hover:text-red-700" data-testid={`record-${r.month_key}`}>
                          Record
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile schedule cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {data.rows.map((r) => (
            <li key={r.month_key} className="p-3.5" data-testid={`loan-mrow-${r.month_key}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[13px] font-semibold text-slate-900">{r.label} {r.year}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    Due <span className="font-mono-fin text-slate-700 tabular-nums">{formatRupees(r.interest_due)}</span>
                    {r.amount_paid > 0 && (
                      <> · Paid <span className="font-mono-fin text-green-700 tabular-nums">{formatRupees(r.amount_paid)}</span></>
                    )}
                    {r.date_paid && <> on {formatDate(r.date_paid)}</>}
                  </div>
                </div>
                <span className={`inline-block text-[9.5px] tracking-wider uppercase px-1.5 py-0.5 border rounded-sm shrink-0 ${STATUS[r.status] || ""}`}>
                  {r.status === "late" ? `LATE ${r.days_late}d` : r.status.toUpperCase()}
                </span>
              </div>
              {canEdit && r.status !== "paid" && (
                <div className="mt-2">
                  <button onClick={() => openDlg(r)}
                    className="inline-flex items-center gap-1 text-[12px] border border-red-200 rounded-md px-3 py-1.5 text-red-700 bg-red-50 active:bg-red-100"
                    data-testid={`record-mobile-${r.month_key}`}>
                    Record payment
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      </>
      )}

      {view === "interest" && (
        <InterestReport report={interestReport} isTaken={isTaken} />
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record loan payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Month</Label><Input value={form.month_key} readOnly className="mt-2 bg-slate-50" /></div>
            <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2" data-testid="pay-amount" /></div>
            <div><Label>Date paid</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2" data-testid="pay-date" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="pay-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InterestReport({ report, isTaken }) {
  if (!report) return <div className="text-slate-500 text-sm">Loading interest report…</div>;
  const s = report.summary;
  return (
    <div className="space-y-4" data-testid="interest-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Metric label="Rate (annual)" value={`${report.rate_percent}%`} tone="text-slate-900" />
        <Metric label="Principal Outstanding" value={formatRupeesCompact(report.principal_outstanding)} tone="text-slate-900" testId="int-outstanding" />
        <Metric label={isTaken ? "Interest Accrued (owed)" : "Interest Accrued (earned)"} value={formatRupeesCompact(s.interest_accrued)} tone="text-slate-900" testId="int-accrued" />
        <Metric label="Interest Due (accrued - paid)" value={formatRupeesCompact(s.interest_due)} tone={s.interest_due > 0 ? "text-red-600" : "text-green-700"} testId="int-due" />
      </div>
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Month</th>
              <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Opening Principal</th>
              <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Interest Accrued</th>
              <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Interest Paid</th>
              <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Closing Principal</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r) => (
              <tr key={r.month_key} className="border-b border-slate-100">
                <td className="p-3 font-medium">{r.label} {r.year}</td>
                <td className="p-3 text-right font-mono-fin text-slate-700">{formatRupees(r.opening_principal)}</td>
                <td className="p-3 text-right font-mono-fin">{formatRupees(r.interest_accrued)}</td>
                <td className={`p-3 text-right font-mono-fin ${r.interest_paid > 0 ? "text-green-700" : "text-slate-400"}`}>
                  {r.interest_paid > 0 ? formatRupees(r.interest_paid) : "-"}
                </td>
                <td className="p-3 text-right font-mono-fin text-slate-700">{formatRupees(r.closing_principal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500">
        Uses simple interest ( P × r ÷ 12 ÷ 100 ) on the outstanding principal each month.
        Principal repayments and interest splits recorded via Receipts/Payments are reflected here.
      </p>
    </div>
  );
}

function Metric({ label, value, tone, testId }) {
  return (
    <div className="p-5 border border-slate-200 bg-white">
      <div className="text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
      <div className={`mt-2 font-mono-fin text-2xl font-semibold ${tone || "text-slate-900"}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function TabBar({ tab, setTab, givenCount, takenCount }) {
  return (
    <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md p-1">
      <button
        onClick={() => setTab("given")}
        data-testid="loan-tab-given"
        className={`px-3 py-1.5 rounded text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
          tab === "given" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
        }`}
      >
        Loans Given <span className={`text-[10px] font-mono-fin ${tab === "given" ? "text-white/70" : "text-slate-400"}`}>{givenCount}</span>
      </button>
      <button
        onClick={() => setTab("taken")}
        data-testid="loan-tab-taken"
        className={`px-3 py-1.5 rounded text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
          tab === "taken" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
        }`}
      >
        Loans Taken <span className={`text-[10px] font-mono-fin ${tab === "taken" ? "text-white/70" : "text-slate-400"}`}>{takenCount}</span>
      </button>
    </div>
  );
}
